import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import * as z from "zod";
import React, { useState } from "react";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { toast } from "../../../components/ui/use-toast";
import { useAuth } from "../../../lib/AuthContext";
import TextController from "../../form-fields/TextController";
import SwitchController from "../../form-fields/SwitchController";
import { QRCodeCanvas } from "qrcode.react";
import {
  enable2FA,
  regenerateTOTP,
  verifyTOTPToken,
  // TODO: Check if this import is actually used - commented out for cleanup
  // validateAndGetBackupCodes,
} from "../../../utils/otp";

// Validation schema for the 2FA form
const twoFactorFormSchema = z.object({
  enable2FA: z.boolean(),
});

const totpValidationSchema = z.object({
  twoFactorCode: z
    .string()
    .length(6, "Code must be 6 digits")
    .regex(/^\d+$/, "Code must contain only numbers"),
});

type TwoFactorFormValues = z.infer<typeof twoFactorFormSchema>;
type TOTPFormValues = z.infer<typeof totpValidationSchema>;

export function TwoFactorAuthForm() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [backupCodes, setBackupCodes] = useState<number[] | null | undefined>(
    null
  );
  const [isTOTPVerified, setIsTOTPVerified] = useState(false); // New flag

  const twoFactorForm = useForm<TwoFactorFormValues>({
    resolver: zodResolver(twoFactorFormSchema),
    defaultValues: { enable2FA: currentUser?.totp_enabled },
  });

  const totpForm = useForm<TOTPFormValues>({
    resolver: zodResolver(totpValidationSchema),
    defaultValues: { twoFactorCode: "" },
  });

  const handle2FASubmit = async (data: TwoFactorFormValues) => {
    setLoading(true);
    try {
      if (!currentUser?.id) {
        toast({
          title: "Error",
          description: "User ID is missing. Please log in and try again.",
        });
        return;
      }

      if (data.enable2FA) {
        const { secret, otpauthUrl } = await enable2FA();
        if (!secret || !otpauthUrl) throw new Error("Failed to retrieve TOTP.");
        setSecret(secret);
        setQrCodeUrl(otpauthUrl);
        setModalOpen(true);

        toast({
          title: "2FA Enabled",
          description: "Scan the QR code and confirm your code.",
        });
      } else {
        // When "disabling" 2FA, actually regenerate it with new codes
        const { secret, otpauthUrl } = await regenerateTOTP();
        if (!secret || !otpauthUrl)
          throw new Error("Failed to regenerate TOTP.");

        // Clear previous state and set new TOTP data
        setSecret(secret);
        setQrCodeUrl(otpauthUrl);
        setBackupCodes(null);
        setIsTOTPVerified(false);
        setModalOpen(true);

        toast({
          title: "2FA Regenerated",
          description:
            "New QR code generated. Scan with your authenticator app and confirm the new code.",
        });
      }
    } catch (error) {
      console.error("Error enabling/disabling 2FA:", error);
      toast({
        title: "Update Failed",
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTOTPVerify = async (data: TOTPFormValues) => {
    setLoading(true);
    try {
      const result = await verifyTOTPToken(data.twoFactorCode);
      if (result.valid) {
        setBackupCodes(result.backup_codes);
        toast({
          title: "Verification Successful",
          description: "The code is valid. Backup codes generated.",
        });
        setIsTOTPVerified(true); // Mark the code as verified
      } else {
        toast({
          title: "Verification Failed",
          description: "The code is incorrect. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error verifying TOTP code:", error);
      toast({
        title: "Verification Failed",
        description:
          "An error occurred while verifying the code. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyBackupCodes = () => {
    if (backupCodes) {
      const codesString = backupCodes.join("\n");
      navigator.clipboard
        .writeText(codesString)
        .then(() => {
          toast({
            title: "Copied",
            description: "Backup codes copied to clipboard.",
          });
        })
        .catch(() => {
          toast({
            title: "Error",
            description: "Failed to copy backup codes. Please try again.",
          });
        });
    }
  };

  return (
    <FormProvider {...twoFactorForm}>
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-accent mb-4">
          Two-Factor Authentication
        </h2>

        {backupCodes && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-accent">Backup Codes</h3>
            <p className="text-sm text-muted-foreground">
              Save these codes in a secure place. Each code can be used only
              once if you lose access to your authenticator app.
            </p>
            <div className="grid grid-cols-2 gap-4 bg-gray-100 p-4 rounded-md">
              {backupCodes.map((code, index) => (
                <div key={index} className="text-sm font-mono text-gray-700">
                  {code}
                </div>
              ))}
            </div>
            <Button
              onClick={handleCopyBackupCodes}
              className="w-full mt-2 bg-primary text-white"
            >
              Copy Backup Codes
            </Button>
          </div>
        )}
        <form
          onSubmit={twoFactorForm.handleSubmit(handle2FASubmit)}
          className="space-y-6"
        >
          <SwitchController
            name="enable2FA"
            label="Two-Factor Authentication Status"
            defaultValue={false}
            infoMessage="Toggle on to enable 2FA, toggle off to regenerate with new codes (previous codes will be invalidated)"
          />
          <Button type="submit" className="bg-primary" disabled={loading}>
            {loading ? "Updating..." : "Update 2FA Settings"}
          </Button>
        </form>

        <Dialog
          open={modalOpen}
          onOpenChange={(open) => {
            if (open || isTOTPVerified) {
              setModalOpen(open);
            } else {
              toast({
                title: "Action Required",
                description: "Please complete verification before closing.",
              });
            }
          }}
        >
          <DialogContent className="w-[400px]">
            <DialogHeader>
              <DialogTitle>Two-Factor Authentication Setup</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {qrCodeUrl && (
                <div className="flex flex-col items-center space-y-2">
                  <QRCodeCanvas value={qrCodeUrl} size={200} />
                  <p className="text-sm text-gray-400">
                    Scan this QR code with your Authenticator app and input the
                    6-digit code to complete two-factor authentication setup.
                  </p>
                </div>
              )}
              <FormProvider {...totpForm}>
                <form
                  onSubmit={totpForm.handleSubmit(handleTOTPVerify)}
                  className="space-y-4"
                >
                  <TextController
                    name="twoFactorCode"
                    label="Confirmation Code"
                    placeholder="Enter the 6-digit code"
                    showPrefix={false}
                    infoMessage="Enter the code generated by your Authenticator app."
                  />
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-primary"
                    >
                      {loading ? "Verifying..." : "Verify Code"}
                    </Button>
                  </DialogFooter>
                </form>
              </FormProvider>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </FormProvider>
  );
}
